import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Shield, Link2, Sparkles, Download, Phone, Video, Lock, Moon, Sun, Users, Zap, Heart, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

// Simplified welcome for native app (Capacitor/APK)
const NativeWelcome = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden" data-testid="native-welcome-page">
      {/* Night sky background */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #0a0a1a 0%, #0d1b2a 40%, #1b2838 100%)' }}>
        {/* Stars */}
        {[...Array(40)].map((_, i) => {
          const size = i % 7 === 0 ? 2.5 : i % 3 === 0 ? 1.5 : 1;
          const isGold = i % 5 === 0;
          return (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                width: `${size}px`,
                height: `${size}px`,
                left: `${(i * 23 + 7) % 96}%`,
                top: `${(i * 17 + 3) % 94}%`,
                opacity: 0.2 + (i % 4) * 0.15,
                background: isGold ? 'rgba(212,175,55,0.9)' : 'white',
                boxShadow: isGold ? '0 0 4px rgba(212,175,55,0.6)' : 'none',
                animation: `nwTwinkle ${2 + (i % 3) * 1.5}s ease-in-out infinite ${i * 0.3}s`
              }}
            />
          );
        })}
        {/* Aurora */}
        <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.2 }}>
          <div className="absolute w-[200%] h-full" style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(0,255,128,0.08) 20%, rgba(0,200,255,0.1) 40%, transparent 60%, rgba(212,175,55,0.06) 80%, transparent 100%)',
            animation: 'nwAurora 20s ease-in-out infinite',
            filter: 'blur(16px)'
          }} />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center px-8 text-center">
        {/* Logo */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#F4C430] flex items-center justify-center shadow-lg shadow-amber-500/30 mb-6"
        >
          <MessageCircle size={40} className="text-white" />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-4xl font-heading text-white mb-3 tracking-tight"
        >
          Chat<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#F4C430]">Buzzz</span>
        </motion.h1>

        {/* Tagline */}
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.35 }}
          className="text-gray-400 text-base mb-12"
        >
          Private. Secure. Exclusive.
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col gap-3 w-full max-w-[280px]"
        >
          <button
            data-testid="native-get-started-btn"
            onClick={() => navigate('/register')}
            className="h-14 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F4C430] text-white font-semibold shadow-lg shadow-amber-500/25 hover:shadow-xl active:scale-[0.97] transition-all duration-200"
          >
            Create Account
          </button>
          <button
            data-testid="native-sign-in-btn"
            onClick={() => navigate('/login')}
            className="h-14 rounded-full font-semibold text-white bg-white/10 border border-white/15 hover:bg-white/15 active:scale-[0.97] transition-all duration-200"
          >
            Sign In
          </button>
        </motion.div>
      </div>

      <style>{`
        @keyframes nwTwinkle { 0%, 100% { opacity: 0.1; } 50% { opacity: 0.8; } }
        @keyframes nwAurora { 0% { transform: translateX(0%); } 50% { transform: translateX(-25%); } 100% { transform: translateX(0%); } }
      `}</style>
    </div>
  );
};

const Welcome = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const isNativeApp = !!window.Capacitor?.isNativePlatform?.();

  // Show simplified welcome on native app
  if (isNativeApp) {
    return <NativeWelcome />;
  }

  const handleDownloadAPK = () => {
    window.open('https://www.chatbuzzz.com/apk/ChatBuzzz.apk', '_blank');
  };

  // Colorful images for the collage
  const images = [
    { 
      src: "https://images.unsplash.com/photo-1621552866397-9a3d8e466496?w=600&h=800&fit=crop", 
      alt: "Woman with phone on colorful background",
      style: "top-0 left-0 w-64 h-72 z-10"
    },
    { 
      src: "https://images.unsplash.com/photo-1508166785545-c2dd4c113c66?w=500&h=600&fit=crop", 
      alt: "Friends celebrating with phones",
      style: "top-24 right-0 w-52 h-60 z-20"
    },
    { 
      src: "https://images.unsplash.com/photo-1687360441027-27e70655b27e?w=400&h=500&fit=crop", 
      alt: "Friends laughing with phones",
      style: "bottom-0 left-8 w-48 h-56 z-15"
    }
  ];

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-500 overflow-hidden ${
      isDarkMode 
        ? 'bg-gray-950' 
        : 'bg-white'
    }`} data-testid="welcome-page">
      
      {/* Animated Background Gradient Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ 
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className={`absolute -top-40 -left-40 w-96 h-96 rounded-full blur-3xl opacity-30 ${
            isDarkMode ? 'bg-amber-500' : 'bg-amber-200'
          }`}
        />
        <motion.div
          animate={{ 
            x: [0, -80, 0],
            y: [0, 100, 0],
            scale: [1, 1.3, 1]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className={`absolute top-1/3 -right-32 w-80 h-80 rounded-full blur-3xl opacity-25 ${
            isDarkMode ? 'bg-purple-600' : 'bg-purple-300'
          }`}
        />
        <motion.div
          animate={{ 
            x: [0, 60, 0],
            y: [0, -80, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 4 }}
          className={`absolute bottom-20 left-1/4 w-72 h-72 rounded-full blur-3xl opacity-20 ${
            isDarkMode ? 'bg-cyan-500' : 'bg-cyan-200'
          }`}
        />
        <motion.div
          animate={{ 
            x: [0, -40, 0],
            y: [0, 60, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className={`absolute -bottom-20 right-1/4 w-64 h-64 rounded-full blur-3xl opacity-25 ${
            isDarkMode ? 'bg-rose-500' : 'bg-rose-200'
          }`}
        />
      </div>

      {/* Navigation Bar */}
      <nav className={`relative z-50 px-6 py-4 ${isDarkMode ? 'bg-gray-950/80' : 'bg-white/80'} backdrop-blur-md`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#F4C430] flex items-center justify-center shadow-lg shadow-amber-500/20">
              <MessageCircle size={22} className="text-white" />
            </div>
            <span className={`font-heading text-2xl ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Chat<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#F4C430]">Buzzz</span>
            </span>
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={handleDownloadAPK}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                isDarkMode 
                  ? 'text-gray-300 hover:text-white hover:bg-white/10' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              data-testid="nav-download-btn"
            >
              <Download size={16} />
              Get the App
            </button>
            
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              className={`h-10 w-10 rounded-full flex items-center justify-center transition-all ${
                isDarkMode 
                  ? 'bg-gray-800 hover:bg-gray-700 text-yellow-400' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <button
              onClick={() => navigate('/login')}
              data-testid="nav-signin-btn"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                isDarkMode 
                  ? 'text-white hover:bg-white/10' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Sign In
            </button>

            <button
              onClick={() => navigate('/register')}
              data-testid="nav-getstarted-btn"
              className="px-5 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-[#D4AF37] to-[#F4C430] text-white shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 transition-all hover:scale-105"
            >
              Get Started
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden h-10 w-10 rounded-full flex items-center justify-center ${
              isDarkMode ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700'
            }`}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`md:hidden absolute top-full left-0 right-0 p-4 ${
              isDarkMode ? 'bg-gray-900' : 'bg-white'
            } border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-100'} shadow-xl`}
          >
            <div className="flex flex-col gap-3">
              <button
                onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                className={`w-full py-3 rounded-xl font-medium ${
                  isDarkMode ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                className="w-full py-3 rounded-xl font-semibold bg-gradient-to-r from-[#D4AF37] to-[#F4C430] text-white"
              >
                Get Started
              </button>
              <button
                onClick={() => { handleDownloadAPK(); setMobileMenuOpen(false); }}
                className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 ${
                  isDarkMode ? 'bg-green-600 text-white' : 'bg-green-500 text-white'
                }`}
              >
                <Download size={18} />
                Download App
              </button>
              <button
                onClick={toggleTheme}
                className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 ${
                  isDarkMode ? 'bg-gray-800 text-yellow-400' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <div className="relative z-10 flex-1 flex flex-col lg:flex-row items-center justify-center px-6 py-12 lg:py-20 gap-12 lg:gap-20 max-w-7xl mx-auto w-full">
        {/* Left Side - Content */}
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-center lg:text-left max-w-xl flex-1"
        >
          {/* Badge */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 ${
              isDarkMode 
                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' 
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}
          >
            <Sparkles size={14} />
            Invite-Only Premium Messaging
          </motion.div>
          
          <h1 className={`font-heading text-4xl sm:text-5xl lg:text-6xl mb-6 tracking-tight leading-tight ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Connect with your{' '}
            <span className="relative">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] via-[#F4C430] to-[#FFD700]">
                inner circle
              </span>
              <motion.span
                animate={{ width: ['0%', '100%', '100%', '0%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-1 left-0 h-1 bg-gradient-to-r from-[#D4AF37] to-[#F4C430] rounded-full"
              />
            </span>
          </h1>
          
          <p className={`text-base lg:text-lg mb-8 leading-relaxed ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            End-to-end encrypted messaging, crystal-clear video calls, and exclusive features designed for meaningful conversations.
          </p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
          >
            <button
              data-testid="get-started-btn"
              onClick={() => navigate('/register')}
              className="group h-14 px-8 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F4C430] text-white font-semibold shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/35 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2"
            >
              Start Chatting Free
              <motion.span
                animate={{ x: [0, 4, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <Sparkles size={18} />
              </motion.span>
            </button>
            
            <button
              data-testid="sign-in-btn"
              onClick={() => navigate('/login')}
              className={`h-14 px-8 rounded-full font-semibold transition-all duration-300 flex items-center justify-center border-2 ${
                isDarkMode 
                  ? 'bg-white/5 text-white border-white/10 hover:bg-white/10 hover:border-white/20' 
                  : 'bg-white text-gray-800 border-gray-200 hover:border-[#D4AF37] hover:text-[#D4AF37] shadow-sm'
              }`}
            >
              Sign In
            </button>
          </motion.div>

          {/* Download APK & Link Device */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 flex flex-wrap gap-4 justify-center lg:justify-start"
          >
            <button
              data-testid="download-apk-btn"
              onClick={handleDownloadAPK}
              className={`h-11 px-5 rounded-full font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                isDarkMode 
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20' 
                  : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
              }`}
            >
              <Download size={16} />
              Android APK
            </button>

            <button
              data-testid="link-device-btn"
              onClick={() => navigate('/link-device')}
              className={`h-11 px-5 rounded-full font-medium transition-all flex items-center justify-center gap-2 ${
                isDarkMode 
                  ? 'text-gray-400 hover:text-white hover:bg-white/5' 
                  : 'text-gray-500 hover:text-[#D4AF37] hover:bg-gray-50'
              }`}
            >
              <Link2 size={16} />
              Link Device
            </button>
          </motion.div>

          {/* Social Proof */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className={`mt-10 flex items-center gap-4 justify-center lg:justify-start ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}
          >
            <div className="flex -space-x-2">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                    isDarkMode ? 'border-gray-950' : 'border-white'
                  }`}
                  style={{
                    background: ['#D4AF37', '#8B5CF6', '#EC4899', '#10B981'][i],
                    color: 'white'
                  }}
                >
                  {['J', 'M', 'A', 'K'][i]}
                </div>
              ))}
            </div>
            <span className="text-sm">
              Join <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>2,500+</strong> premium members
            </span>
          </motion.div>
        </motion.div>

        {/* Right Side - Images Collage */}
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="relative hidden lg:block flex-1 max-w-lg"
        >
          <div className="relative w-full h-[520px]">
            {/* Main Image */}
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-0 left-0 w-64 h-80 rounded-3xl overflow-hidden shadow-2xl z-10"
              style={{ 
                border: isDarkMode ? '4px solid rgba(255,255,255,0.1)' : '4px solid white',
                boxShadow: isDarkMode 
                  ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' 
                  : '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
              }}
            >
              <img 
                src="https://images.unsplash.com/photo-1621552866397-9a3d8e466496?w=600&h=800&fit=crop" 
                alt="Woman with phone on colorful background"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </motion.div>

            {/* Second Image */}
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              className="absolute top-20 right-0 w-56 h-72 rounded-3xl overflow-hidden shadow-2xl z-20"
              style={{ 
                border: isDarkMode ? '4px solid rgba(255,255,255,0.1)' : '4px solid white',
                boxShadow: isDarkMode 
                  ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' 
                  : '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
              }}
            >
              <img 
                src="https://images.unsplash.com/photo-1508166785545-c2dd4c113c66?w=500&h=700&fit=crop" 
                alt="Friends celebrating"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </motion.div>

            {/* Third Image */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute bottom-0 left-16 w-48 h-56 rounded-3xl overflow-hidden shadow-2xl z-15"
              style={{ 
                border: isDarkMode ? '4px solid rgba(255,255,255,0.1)' : '4px solid white',
                boxShadow: isDarkMode 
                  ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' 
                  : '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
              }}
            >
              <img 
                src="https://images.unsplash.com/photo-1687360441027-27e70655b27e?w=400&h=500&fit=crop" 
                alt="Friends laughing"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </motion.div>

            {/* Floating Feature Icons */}
            <motion.div
              animate={{ 
                scale: [1, 1.15, 1],
                rotate: [0, 5, 0]
              }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute -top-2 right-24 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#F4C430] flex items-center justify-center shadow-lg shadow-amber-500/30 z-30"
            >
              <Video size={24} className="text-white" />
            </motion.div>

            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, -5, 0]
              }}
              transition={{ duration: 2.5, repeat: Infinity, delay: 0.3 }}
              className="absolute bottom-28 -left-2 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 z-30"
            >
              <Phone size={20} className="text-white" />
            </motion.div>

            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
              }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
              className="absolute bottom-10 right-16 w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-violet-700 flex items-center justify-center shadow-lg shadow-violet-500/30 z-30"
            >
              <Lock size={18} className="text-white" />
            </motion.div>

            <motion.div
              animate={{ 
                scale: [1, 1.12, 1],
              }}
              transition={{ duration: 2.8, repeat: Infinity, delay: 1 }}
              className="absolute top-48 -right-4 w-10 h-10 rounded-lg bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center shadow-lg shadow-rose-500/30 z-30"
            >
              <Heart size={16} className="text-white" />
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Features Section */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.8 }}
        className={`relative z-10 py-20 px-6 ${
          isDarkMode 
            ? 'bg-gradient-to-b from-gray-950 via-gray-900/50 to-gray-950' 
            : 'bg-gradient-to-b from-white via-gray-50 to-white'
        }`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}
            >
              PREMIUM FEATURES
            </motion.p>
            <h2 className={`text-3xl md:text-4xl font-heading ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Why choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#F4C430]">ChatBuzzz</span>?
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'End-to-End Encrypted', desc: 'Military-grade encryption keeps your conversations private', gradient: 'from-violet-500 to-purple-600', shadow: 'shadow-violet-500/20' },
              { icon: Video, title: 'HD Video Calls', desc: 'Crystal clear video with no time limits', gradient: 'from-sky-400 to-blue-600', shadow: 'shadow-sky-500/20' },
              { icon: Users, title: 'Invite Only', desc: 'Exclusive community for meaningful connections', gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-amber-500/20' },
              { icon: Zap, title: 'Lightning Fast', desc: 'Real-time messaging with instant delivery', gradient: 'from-emerald-400 to-teal-600', shadow: 'shadow-emerald-500/20' }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.9 + index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className={`group relative rounded-3xl p-6 transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/10' 
                    : 'bg-white border border-gray-100 shadow-lg hover:shadow-2xl'
                }`}
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-5 shadow-lg ${feature.shadow} group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon size={26} className="text-white" />
                </div>
                <h3 className={`font-heading text-xl mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {feature.title}
                </h3>
                <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className={`relative z-10 py-20 px-6 ${
          isDarkMode ? 'bg-gray-950' : 'bg-white'
        }`}
      >
        <div className={`max-w-4xl mx-auto text-center rounded-3xl p-12 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-amber-500/10 via-purple-500/10 to-cyan-500/10 border border-white/10' 
            : 'bg-gradient-to-br from-amber-50 via-purple-50 to-cyan-50 border border-gray-100'
        }`}>
          <h2 className={`text-3xl md:text-4xl font-heading mb-4 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Ready to join the conversation?
          </h2>
          <p className={`mb-8 max-w-lg mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Get your invite and start connecting with people who matter most.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="h-14 px-10 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F4C430] text-white font-semibold shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/35 hover:scale-105 transition-all duration-300"
            >
              Get Your Invite
            </button>
            <button
              onClick={handleDownloadAPK}
              className={`h-14 px-8 rounded-full font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-white/10 text-white hover:bg-white/20 border border-white/10' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Download size={18} />
              Download App
            </button>
          </div>
        </div>
      </motion.div>

      {/* Footer */}
      <footer className={`relative z-10 py-8 px-6 border-t ${
        isDarkMode ? 'bg-gray-950 border-gray-800 text-gray-500' : 'bg-white border-gray-100 text-gray-400'
      }`}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#F4C430] flex items-center justify-center">
              <MessageCircle size={16} className="text-white" />
            </div>
            <span className="font-heading text-lg">
              Chat<span className="text-[#D4AF37]">Buzzz</span>
            </span>
          </div>
          <p className="text-sm">
            © 2026 ChatBuzzz. Your privacy, our priority.
          </p>
          <div className="flex items-center gap-4">
            <button onClick={toggleTheme} className="text-sm hover:text-[#D4AF37] transition-colors">
              {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Welcome;
